import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "@/config/axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, Database, RefreshCw, Trash2, CheckCircle, AlertCircle, Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActorEmailKnowledgeBase() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [automationRules, setAutomationRules] = useState([]);

  useEffect(() => {
    fetchStatus();
    fetchPreview();
    fetchAutomationRules();
  }, [actorId]);

  useEffect(() => {
    // Poll for status if building
    if (building || status?.status === "building") {
      const interval = setInterval(() => {
        fetchStatus();
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [building, status]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        `${API}/admin/actors/${actorId}/knowledge-base/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(response.data);
      
      if (response.data.status === "completed") {
        setBuilding(false);
        if (!preview) {
          fetchPreview();
        }
      }
    } catch (error) {
      console.error("Failed to fetch KB status", error);
      toast.error("Failed to load knowledge base status");
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        `${API}/admin/actors/${actorId}/knowledge-base/preview?limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreview(response.data);
    } catch (error) {
      console.error("Failed to fetch KB preview", error);
    }
  };

  const fetchAutomationRules = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        `${API}/admin/ai-email/rules-v2/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Filter only active rules
      const activeRules = response.data.rules?.filter(rule => rule.active) || [];
      setAutomationRules(activeRules);
    } catch (error) {
      console.error("Failed to fetch automation rules", error);
    }
  };

  const buildKnowledgeBase = async (fullRebuild = false) => {
    try {
      setBuilding(true);
      const token = localStorage.getItem("token");
      await axiosInstance.post(
        `${API}/admin/actors/${actorId}/knowledge-base/build`,
        { full_rebuild: fullRebuild },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const message = fullRebuild 
        ? "Full KB rebuild started - this may take several minutes" 
        : "Knowledge base update started for new emails";
      toast.success(message);
    } catch (error) {
      console.error("Failed to build KB", error);
      toast.error("Failed to build knowledge base");
      setBuilding(false);
    }
  };

  const deleteKnowledgeBase = async () => {
    if (!window.confirm("Are you sure you want to delete the knowledge base? This action cannot be undone.")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(
        `${API}/admin/actors/${actorId}/knowledge-base`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Knowledge base deleted");
      await fetchStatus();
      setPreview(null);
      setSelectedEntry(null);
    } catch (error) {
      console.error("Failed to delete KB", error);
      toast.error("Failed to delete knowledge base");
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/ai/actors/manage/${actorId}`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Manage
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Database className="w-6 h-6" />
              Email Knowledge Base
            </h1>
            <p className="text-sm text-muted-foreground">
              RAG-powered semantic search for AI email assistant
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {!status?.exists && (
            <Button
              variant="default"
              size="sm"
              onClick={() => buildKnowledgeBase(true)}
              disabled={building}
            >
              <Database className={`w-4 h-4 mr-2 ${building ? 'animate-spin' : ''}`} />
              Build Knowledge Base
            </Button>
          )}
          
          {status?.exists && status?.status === "completed" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => buildKnowledgeBase(false)}
                disabled={building}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${building ? 'animate-spin' : ''}`} />
                Update (New Emails)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => buildKnowledgeBase(true)}
                disabled={building}
              >
                <Database className={`w-4 h-4 mr-2 ${building ? 'animate-spin' : ''}`} />
                Full Rebuild
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={deleteKnowledgeBase}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle>Knowledge Base Status</CardTitle>
          <CardDescription>Current state and statistics</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!status?.exists ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
              <h3 className="text-lg font-semibold mb-2">Knowledge Base Not Created</h3>
              <p className="text-muted-foreground mb-4">
                Build the knowledge base to enable semantic search for your AI email assistant.
                This will process all {status?.total_emails || 0} emails and create searchable embeddings.
              </p>
              <Button onClick={buildKnowledgeBase} disabled={building}>
                {building ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Building... {status?.progress || 0}%
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Build Knowledge Base
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="flex items-center gap-2">
                    {status.status === "completed" ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : status.status === "building" ? (
                      <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-500" />
                    )}
                    <Badge variant={status.status === "completed" ? "default" : "secondary"}>
                      {status.status}
                    </Badge>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Total Emails</p>
                  <p className="text-2xl font-bold">{status.total_emails || 0}</p>
                </div>
                
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">KB Entries</p>
                  <p className="text-2xl font-bold">{status.total_entries || 0}</p>
                </div>
                
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Progress</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `${status.progress || 0}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{status.progress || 0}%</span>
                  </div>
                </div>
              </div>
              
              {/* Email Statistics Row */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Unread Emails</p>
                  <p className="text-2xl font-bold text-blue-600">{status.unread_count || 0}</p>
                </div>
                
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Read Emails</p>
                  <p className="text-2xl font-bold text-green-600">{status.read_count || 0}</p>
                </div>
                
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Archived Emails</p>
                  <p className="text-2xl font-bold text-gray-600">{status.archived_count || 0}</p>
                </div>
              </div>
            </div>
          )}
          
          {status?.last_updated && (
            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                Last updated: {new Date(status.last_updated).toLocaleString()}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Real-Time Email Statistics */}
      {status?.statistics && status.status === "completed" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Unread Emails */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Unread Emails</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">
                {status.statistics.unread_count || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Updates when emails are marked as read
              </p>
            </CardContent>
          </Card>

          {/* Read Emails */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Read Emails</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {status.statistics.read_count || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Updates in real-time
              </p>
            </CardContent>
          </Card>

          {/* Archived Emails */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Archived Emails</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {status.statistics.archived_count || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                In archive folder
              </p>
            </CardContent>
          </Card>

          {/* Unique Senders */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Unique Senders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {status.statistics.unique_senders_count || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Total distinct email addresses
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sender Statistics - ALL SENDERS */}
      {status?.statistics?.sender_statistics && Object.keys(status.statistics.sender_statistics).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>All Senders ({Object.keys(status.statistics.sender_statistics).length})</CardTitle>
            <CardDescription>Complete list of unique senders with email counts, read/unread breakdown, and date ranges</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-[600px] overflow-y-auto space-y-2">
              {Object.entries(status.statistics.sender_statistics)
                .sort((a, b) => b[1].count - a[1].count)
                .map(([sender, data], idx) => {
                  const firstDate = data.first_email ? new Date(data.first_email) : null;
                  const latestDate = data.latest_email ? new Date(data.latest_email) : null;
                  const readCount = data.count - (data.unread_count || 0);
                  
                  return (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{sender}</p>
                        <p className="text-xs text-muted-foreground">
                          {firstDate && latestDate ? (
                            <>
                              From {firstDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} 
                              {' to '}
                              {latestDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                              {firstDate.getTime() === latestDate.getTime() && ' (single email)'}
                            </>
                          ) : (
                            'Date range unavailable'
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 ml-4">
                        <div className="text-right">
                          <p className="font-bold text-blue-600">{data.count}</p>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{readCount}</p>
                          <p className="text-xs text-muted-foreground">Read</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-orange-600">{data.unread_count || 0}</p>
                          <p className="text-xs text-muted-foreground">Unread</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tag Statistics */}
      {status?.statistics?.tag_statistics && Object.keys(status.statistics.tag_statistics).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tag Statistics</CardTitle>
            <CardDescription>Email counts per tag with latest email dates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
              {Object.entries(status.statistics.tag_statistics).map(([tag, data], idx) => (
                <div key={idx} className="border rounded p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="secondary" className="text-sm">{tag}</Badge>
                    <span className="text-2xl font-bold text-blue-600">{data.count}</span>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium">Latest email:</span>{' '}
                      {data.latest_email ? new Date(data.latest_email).toLocaleString() : 'N/A'}
                    </div>
                    {data.latest_subject && (
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">Subject:</span> {data.latest_subject.substring(0, 50)}...
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Automation Rules and Tags */}
      {(automationRules.length > 0 || preview) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Active Automation Rules */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Active Automation Rules ({automationRules.length})</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/ai/actors/${actorId}/automation-rules`)}
                >
                  Manage
                </Button>
              </div>
              <CardDescription>Rules automatically applied to emails</CardDescription>
            </CardHeader>
            <CardContent>
              {automationRules.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {automationRules.map((rule) => (
                    <div key={rule.id} className="p-3 border rounded hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-medium text-sm">{rule.name}</p>
                        <Badge variant="default" className="bg-green-500 text-xs">Active</Badge>
                      </div>
                      {rule.description && (
                        <p className="text-xs text-muted-foreground mb-2">{rule.description}</p>
                      )}
                      <div className="space-y-1">
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs font-medium">When:</span>
                          {rule.conditions?.slice(0, 2).map((cond, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {cond.type}: {cond.value}
                            </Badge>
                          ))}
                          {rule.conditions?.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{rule.conditions.length - 2} more
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs font-medium">Then:</span>
                          {rule.actions?.slice(0, 2).map((action, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {action.type}
                            </Badge>
                          ))}
                          {rule.actions?.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{rule.actions.length - 2} more
                            </Badge>
                          )}
                        </div>
                      </div>
                      {rule.statistics && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="text-xs text-muted-foreground">
                            Processed: {rule.statistics.total_processed || 0} emails
                            {rule.statistics.last_run && ` • Last run: ${new Date(rule.statistics.last_run).toLocaleDateString()}`}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground mb-2">No active automation rules</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/ai/actors/${actorId}/automation-rules`)}
                  >
                    Create Rules
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>All Tags ({preview?.all_tags?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              {preview?.all_tags && preview.all_tags.length > 0 ? (
                <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                  {preview.all_tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline">{tag}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No tags</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Email Entries Preview */}
      {preview && preview.entries && preview.entries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Knowledge Base Entries (Sample)</CardTitle>
            <CardDescription>
              Showing {preview.entries.length} sample entries in markdown format
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4">
              {preview.entries.map((entry, idx) => (
                <div 
                  key={idx} 
                  className="border rounded p-4 hover:bg-slate-50 cursor-pointer transition"
                  onClick={() => setSelectedEntry(entry)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-sm">{entry.metadata?.subject || "No Subject"}</p>
                      <p className="text-xs text-muted-foreground">{entry.metadata?.from || "Unknown"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">
                        {entry.metadata?.date && new Date(entry.metadata.date).toLocaleDateString()}
                      </p>
                      {entry.metadata?.tags && entry.metadata.tags.length > 0 && (
                        <div className="flex gap-1 mt-1 justify-end">
                          {entry.metadata.tags.map((tag, tagIdx) => (
                            <Badge key={tagIdx} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-xs text-muted-foreground line-clamp-3">
                    {entry.markdown_content?.substring(0, 200)}...
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal for full markdown view */}
      {selectedEntry && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedEntry(null)}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">{selectedEntry.metadata?.subject}</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedEntry(null)}>
                Close
              </Button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                {selectedEntry.markdown_content}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
